import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiBookOpen, FiMonitor, FiSmartphone } from "react-icons/fi";
import { auth, db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import PageLayout from "./components/PageLayout";

const getVehicleTitle = (car = {}) =>
  car.name ||
  `${car.year || ""} ${car.make || ""} ${car.model || ""}`.trim() ||
  "Vehicle";

function ShowTools() {
  const { username, carId } = useParams();
  const navigate = useNavigate();

  const [car, setCar] = useState(null);
  const [pageError, setPageError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (!isMounted) return;

      try {
        setLoading(true);
        setPageError("");

        if (!currentUser) {
          navigate("/auth", { replace: true });
          return;
        }

        if (!username || !carId) {
          setPageError("Page unavailable.");
          return;
        }

        const docRef = doc(db, "profiles", username.toLowerCase());
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          setPageError("Page unavailable.");
          return;
        }

        const data = docSnap.data();

        if (currentUser.uid !== data.userId) {
          navigate(`/user/${username}/car/${carId}`, { replace: true });
          return;
        }

        const foundCar = data.garageCars?.find(
          (item) => String(item.id) === String(carId)
        );

        if (!foundCar) {
          setPageError("Page unavailable.");
          return;
        }

        setCar(foundCar);
      } catch (error) {
        console.log("Show Tools load failed:", error);
        setPageError("Page unavailable.");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [username, carId, navigate]);

  if (loading) {
    return (
      <PageLayout maxWidth="760px" showBack showBottomNav={false}>
        <div style={loadingStyle}>Loading Show Tools...</div>
      </PageLayout>
    );
  }

  if (pageError || !car) {
    return (
      <PageLayout maxWidth="760px" showBack showBottomNav={false}>
        <div style={unavailableStyle}>Page unavailable.</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout maxWidth="860px" showBack showBottomNav={false}>
      <div style={headerStyle}>
        <p style={eyebrowStyle}>Show Tools</p>
        <h1 style={titleStyle}>Create your show-ready display</h1>
        <p style={subtitleStyle}>
          Launch owner-only tools for build identities, show boards, and future
          presentation features.
        </p>
        <p style={vehicleStyle}>{getVehicleTitle(car)}</p>
      </div>

      <div style={toolGridStyle}>
        <article style={activeToolCardStyle}>
          <div style={iconWrapStyle}>
            <FiSmartphone aria-hidden="true" />
          </div>

          <div style={cardContentStyle}>
            <h2 style={cardTitleStyle}>Identity Studio</h2>
            <p style={cardTextStyle}>
              Design a show-ready build identity for displays, stickers, mirror tags, and sharing.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate(`/user/${username}/car/${carId}/showcard`)}
            style={launchButtonStyle}
          >
            Launch Identity Studio
          </button>
        </article>

        <article style={activeToolCardStyle}>
          <div style={iconWrapStyle}>
            <FiMonitor aria-hidden="true" />
          </div>

          <div style={cardContentStyle}>
            <h2 style={cardTitleStyle}>Show Display</h2>
            <p style={cardTextStyle}>
              Generate a professional printable display board for your vehicle.
            </p>
          </div>

          <button
            type="button"
            onClick={() =>
              navigate(`/user/${username}/car/${carId}/show-display`)
            }
            style={launchButtonStyle}
          >
            Launch Show Display
          </button>
        </article>

        <article style={disabledToolCardStyle}>
          <div style={disabledBadgeStyle}>Coming Soon</div>

          <div style={iconWrapStyle}>
            <FiBookOpen aria-hidden="true" />
          </div>

          <div style={cardContentStyle}>
            <h2 style={cardTitleStyle}>Build Book</h2>
            <p style={cardTextStyle}>
              Create a polished book-style presentation of your build history.
            </p>
          </div>

          <button
            type="button"
            disabled
            aria-label="Build Book coming soon"
            style={disabledButtonStyle}
          >
            Coming Soon
          </button>
        </article>
      </div>

      <div style={futureCardStyle}>
        <h2 style={futureTitleStyle}>Future Features</h2>
        <p style={futureTextStyle}>
          More show-ready tools are coming for printable boards, event displays,
          and premium presentation exports.
        </p>
      </div>
    </PageLayout>
  );
}

const loadingStyle = {
  color: "white",
  textAlign: "center",
  padding: "40px"
};

const unavailableStyle = {
  color: "white",
  textAlign: "center",
  padding: "40px",
  backgroundColor: "#181818",
  border: "1px solid #2a2a2a",
  borderRadius: "16px"
};

const headerStyle = {
  marginBottom: "22px"
};

const eyebrowStyle = {
  color: "#e10600",
  fontSize: "12px",
  fontWeight: "900",
  textTransform: "uppercase",
  letterSpacing: "1px",
  margin: "0 0 8px"
};

const titleStyle = {
  color: "white",
  fontSize: "34px",
  lineHeight: "1.05",
  margin: 0,
  fontWeight: "900"
};

const subtitleStyle = {
  color: "#aaa",
  fontSize: "15px",
  lineHeight: "1.5",
  maxWidth: "620px",
  margin: "12px 0 0"
};

const vehicleStyle = {
  color: "#e10600",
  fontSize: "18px",
  fontWeight: "900",
  margin: "16px 0 0"
};

const toolGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: "14px"
};

const activeToolCardStyle = {
  position: "relative",
  backgroundColor: "#181818",
  border: "1px solid rgba(225,6,0,0.55)",
  borderRadius: "18px",
  padding: "18px",
  boxSizing: "border-box",
  color: "white",
  display: "flex",
  flexDirection: "column",
  gap: "14px",
  boxShadow: "0 16px 36px rgba(225,6,0,0.08)"
};

const disabledToolCardStyle = {
  position: "relative",
  backgroundColor: "#181818",
  border: "1px solid #2a2a2a",
  borderRadius: "18px",
  padding: "18px",
  boxSizing: "border-box",
  color: "white",
  display: "flex",
  flexDirection: "column",
  gap: "14px",
  opacity: 0.76
};

const disabledBadgeStyle = {
  position: "absolute",
  top: "14px",
  right: "14px",
  backgroundColor: "#2a2a2a",
  color: "#bbb",
  borderRadius: "999px",
  padding: "5px 8px",
  fontSize: "10px",
  fontWeight: "900",
  textTransform: "uppercase"
};

const iconWrapStyle = {
  width: "42px",
  height: "42px",
  borderRadius: "14px",
  backgroundColor: "#e10600",
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "22px"
};

const cardContentStyle = {
  flex: 1
};

const cardTitleStyle = {
  margin: "0 0 8px",
  fontSize: "21px",
  fontWeight: "900"
};

const cardTextStyle = {
  margin: 0,
  color: "#aaa",
  fontSize: "14px",
  lineHeight: "1.45"
};

const launchButtonStyle = {
  backgroundColor: "#e10600",
  color: "white",
  border: "none",
  borderRadius: "12px",
  padding: "12px 14px",
  fontSize: "14px",
  fontWeight: "900",
  cursor: "pointer"
};

const disabledButtonStyle = {
  backgroundColor: "#333",
  color: "#aaa",
  border: "none",
  borderRadius: "12px",
  padding: "12px 14px",
  fontSize: "14px",
  fontWeight: "900",
  cursor: "not-allowed"
};

const futureCardStyle = {
  backgroundColor: "#181818",
  border: "1px solid #2a2a2a",
  borderRadius: "18px",
  padding: "18px",
  marginTop: "16px",
  color: "white"
};

const futureTitleStyle = {
  margin: "0 0 8px",
  fontSize: "18px",
  fontWeight: "900"
};

const futureTextStyle = {
  margin: 0,
  color: "#aaa",
  lineHeight: "1.5",
  fontSize: "14px"
};

export default ShowTools;
